import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Badge, Spinner, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Moment from 'moment';
import axios from 'axios';
import { device, playerSelector } from '../../actions';
import { HOST } from '../../Config';
import './Sessions.css';

const VERSION = 'v1';
const ENDPOINT = `${HOST}/${VERSION}`;
const TKN_HEAD = 'Bearer';

class Sessions extends Component {
  constructor(props) {
    super(props);

    // デフォルトは現在月
    const initialMonth = Moment();

    this.state = {
      viewMode: 'month', // 'month' または 'all'
      selectedMonth: initialMonth,
      practices: [],
      sessions: [],
      filteredSessions: [],
      selectedDevice: 'all',
      selectedUser: 'all',
      searchKeyword: '',
      isLoading: false,
      currentPage: 1,
      itemsPerPage: 15,
      fetchError: null,
    };
  }

  componentDidMount() {
    if (this.props.getDevices) {
      this.props.getDevices();
    }
    if (this.props.getPlayers) {
      this.props.getPlayers();
    }
    this.fetchSessions();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (!prevProps.authUser && this.props.authUser) ||
      prevState.viewMode !== this.state.viewMode ||
      prevState.selectedMonth !== this.state.selectedMonth ||
      prevState.selectedUser !== this.state.selectedUser
    ) {
      this.fetchSessions();
    }
  }

  getAuthToken = () => {
    return (
      (this.props.authUser && this.props.authUser.token) ||
      localStorage.getItem('token') ||
      ''
    );
  };

  fetchSessions = () => {
    const token = this.getAuthToken();
    if (!token && !this.props.authUser) return;

    this.setState({ isLoading: true, fetchError: null });

    const { viewMode, selectedMonth, selectedUser } = this.state;
    let url = `${ENDPOINT}/practices?limit=500`;

    if (viewMode === 'month') {
      let from = selectedMonth.clone().startOf('month').subtract(1, 'days').format('YYYY-MM-DD');
      let to = selectedMonth.clone().endOf('month').add(1, 'days').format('YYYY-MM-DD');
      url += `&started_at[from]=${from}&started_at[to]=${to}`;
    }

    if (selectedUser !== 'all') {
      url += `&target=${selectedUser}`;
    }

    axios
      .get(url, {
        headers: { Authorization: `${TKN_HEAD} ${token}` },
      })
      .then((response) => {
        const practices = response.data || [];
        const sessions = this.aggregateSessions(practices);

        // もし月別表示で現在月（8月）にデータがなく、全期間で過去にデータがある場合の案内
        this.setState(
          {
            practices,
            sessions,
            isLoading: false,
            currentPage: 1,
          },
          this.applyFilter
        );
      })
      .catch((error) => {
        console.error('Failed to fetch practices for sessions:', error);
        this.setState({
          isLoading: false,
          practices: [],
          sessions: [],
          filteredSessions: [],
          fetchError: error.response?.data?.error || error.message,
        });
      });
  };

  aggregateSessions = (practices) => {
    const sessionMap = new Map();

    practices.forEach((p) => {
      // session_url があればそれをキー、なければ started_at + device_id をキー
      const key = p.session_url || `session_${p.started_at}_${p.device_id}_${p.id}`;

      if (!sessionMap.has(key)) {
        sessionMap.set(key, {
          id: p.id,
          session_url: p.session_url,
          excel_url: p.excel_url,
          device_id: p.device_id,
          device_type: p.device_type,
          user: p.user,
          started_at: p.started_at,
          ended_at: p.ended_at,
          duration: p.duration,
          location: p.location,
          max_speed: p.max_speed || 0,
          total_distance: p.distance || 0,
          practices: [p],
        });
      } else {
        const existing = sessionMap.get(key);
        existing.practices.push(p);
        if (p.max_speed && p.max_speed > existing.max_speed) {
          existing.max_speed = p.max_speed;
        }
        if (p.distance) {
          existing.total_distance += p.distance;
        }
        if (p.excel_url && !existing.excel_url) {
          existing.excel_url = p.excel_url;
        }
        if (p.session_url && !existing.session_url) {
          existing.session_url = p.session_url;
        }
        if (Moment(p.ended_at).isAfter(Moment(existing.ended_at))) {
          existing.ended_at = p.ended_at;
        }
        if (Moment(p.started_at).isBefore(Moment(existing.started_at))) {
          existing.started_at = p.started_at;
        }
      }
    });

    const sessionList = Array.from(sessionMap.values());
    sessionList.sort((a, b) => (Moment(a.started_at).isBefore(Moment(b.started_at)) ? 1 : -1));
    return sessionList;
  };

  applyFilter = () => {
    const { sessions, selectedDevice, searchKeyword } = this.state;
    let filtered = [...sessions];

    if (selectedDevice !== 'all') {
      filtered = filtered.filter((s) => String(s.device_id) === String(selectedDevice));
    }

    if (searchKeyword.trim() !== '') {
      const kw = searchKeyword.toLowerCase();
      filtered = filtered.filter((s) => {
        const userName = s.user ? `${s.user.first_name || ''} ${s.user.last_name || ''}`.toLowerCase() : '';
        const userEmail = s.user?.email ? s.user.email.toLowerCase() : '';
        const deviceId = s.device_id ? String(s.device_id).toLowerCase() : '';
        const location = s.location ? s.location.toLowerCase() : '';
        return (
          userName.includes(kw) ||
          userEmail.includes(kw) ||
          deviceId.includes(kw) ||
          location.includes(kw)
        );
      });
    }

    this.setState({ filteredSessions: filtered, currentPage: 1 });
  };

  handlePrevMonth = () => {
    const newMonth = this.state.selectedMonth.clone().subtract(1, 'month');
    this.setState({ selectedMonth: newMonth });
  };

  handleNextMonth = () => {
    const newMonth = this.state.selectedMonth.clone().add(1, 'month');
    this.setState({ selectedMonth: newMonth });
  };

  handleCurrentMonth = () => {
    const newMonth = Moment();
    this.setState({ selectedMonth: newMonth });
  };

  handleSetMonth = (year, month) => {
    const newMonth = Moment([year, month - 1, 1]);
    this.setState({ selectedMonth: newMonth, viewMode: 'month' });
  };

  handleViewModeChange = (mode) => {
    this.setState({ viewMode: mode });
  };

  handleDeviceChange = (e) => {
    this.setState({ selectedDevice: e.target.value }, this.applyFilter);
  };

  handleUserChange = (e) => {
    this.setState({ selectedUser: e.target.value });
  };

  handleSearchChange = (e) => {
    this.setState({ searchKeyword: e.target.value }, this.applyFilter);
  };

  handleDownloadRaw = (session) => {
    if (session.session_url) {
      window.open(session.session_url, '_blank');
    } else {
      alert('このセッションにはRAWデータURLが存在しません。');
    }
  };

  handleDownloadExcel = (session) => {
    if (session.excel_url) {
      window.open(session.excel_url, '_blank');
    }
  };

  renderPagination = (totalPages) => {
    if (totalPages <= 1) return null;

    const { currentPage } = this.state;
    const items = [];

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => this.setState({ currentPage: currentPage - 1 })}
      />
    );

    for (let page = 1; page <= totalPages; page++) {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 2 && page <= currentPage + 2)
      ) {
        items.push(
          <Pagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => this.setState({ currentPage: page })}
          >
            {page}
          </Pagination.Item>
        );
      } else if (
        page === currentPage - 3 ||
        page === currentPage + 3
      ) {
        items.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
      }
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => this.setState({ currentPage: currentPage + 1 })}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  render() {
    const {
      viewMode,
      selectedMonth,
      filteredSessions,
      selectedDevice,
      selectedUser,
      searchKeyword,
      isLoading,
      currentPage,
      itemsPerPage,
      fetchError,
    } = this.state;

    const { devices, players } = this.props;

    const totalItems = filteredSessions.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentSessions = filteredSessions.slice(startIndex, startIndex + itemsPerPage);

    return (
      <div className="sessions-container">
        <div className="sessions-header">
          <div className="sessions-title">
            <FontAwesomeIcon icon="file-csv" style={{ color: '#0d6efd' }} />
            セッションデータ（RAWデータ）一覧
          </div>
          <div className="sessions-subtitle">
            計測・アップロードされたセッションのRAWデータ（CSV形式）および集計データを一覧からダウンロードできます。
          </div>
        </div>

        {/* フィルターカード */}
        <div className="sessions-filter-card">
          <Row className="align-items-center mb-2">
            {/* 表示モード（月別 / 全期間） */}
            <Col md={5} sm={12} className="mb-2 mb-md-0">
              <div className="d-flex align-items-center">
                <ButtonGroup size="sm" className="mr-3" style={{ marginRight: '12px' }}>
                  <Button
                    variant={viewMode === 'month' ? 'primary' : 'outline-primary'}
                    onClick={() => this.handleViewModeChange('month')}
                  >
                    月別
                  </Button>
                  <Button
                    variant={viewMode === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => this.handleViewModeChange('all')}
                  >
                    全期間
                  </Button>
                </ButtonGroup>

                {viewMode === 'month' && (
                  <div className="d-flex align-items-center">
                    <Button variant="outline-secondary" size="sm" onClick={this.handlePrevMonth}>
                      <FontAwesomeIcon icon="chevron-left" />
                    </Button>
                    <div
                      className="mx-2 font-weight-bold"
                      style={{ fontSize: '1.05rem', minWidth: '120px', textAlign: 'center' }}
                    >
                      {selectedMonth.format('YYYY年 MM月')}
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={this.handleNextMonth} style={{ marginRight: '6px' }}>
                      <FontAwesomeIcon icon="chevron-right" />
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={this.handleCurrentMonth}>
                      今月
                    </Button>
                  </div>
                )}
              </div>
            </Col>

            {/* ユーザー絞り込み */}
            <Col md={3} sm={6} className="mb-2 mb-md-0">
              <Form.Group className="mb-0">
                <Form.Control
                  as="select"
                  size="sm"
                  value={selectedUser}
                  onChange={this.handleUserChange}
                >
                  <option value="all">すべてのユーザー</option>
                  {players &&
                    players.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.last_name} {p.first_name} ({p.email})
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>
            </Col>

            {/* デバイス絞り込み */}
            <Col md={4} sm={6} className="mb-2 mb-md-0">
              <Form.Group className="mb-0">
                <Form.Control
                  as="select"
                  size="sm"
                  value={selectedDevice}
                  onChange={this.handleDeviceChange}
                >
                  <option value="all">すべてのデバイス</option>
                  {devices &&
                    devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.id} {d.category ? `(${d.category})` : ''}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Row className="align-items-center">
            {/* キーワード検索 */}
            <Col md={8} sm={12}>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon="search" />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  type="text"
                  placeholder="ユーザー名、デバイスID、ロケーション等でリアルタイム検索..."
                  value={searchKeyword}
                  onChange={this.handleSearchChange}
                />
                {searchKeyword && (
                  <InputGroup.Append>
                    <Button
                      variant="outline-secondary"
                      onClick={() => this.setState({ searchKeyword: '' }, this.applyFilter)}
                    >
                      ✕
                    </Button>
                  </InputGroup.Append>
                )}
              </InputGroup>
            </Col>

            {/* クイック切替ショートカット */}
            <Col md={4} sm={12} className="text-md-right mt-2 mt-md-0">
              <div className="quick-month-links" style={{ fontSize: '0.85rem' }}>
                <span className="text-muted mr-1">クイック移動: </span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mr-2"
                  style={{ marginRight: '8px' }}
                  onClick={() => this.handleSetMonth(2026, 7)}
                >
                  2026年7月
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mr-2"
                  style={{ marginRight: '8px' }}
                  onClick={() => this.handleSetMonth(2025, 7)}
                >
                  2025年7月
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => this.handleViewModeChange('all')}
                >
                  全件表示
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* 一覧テーブルカード */}
        <div className="sessions-table-card">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">セッションデータを読み込み中...</div>
            </div>
          ) : fetchError ? (
            <div className="sessions-empty text-danger">
              <h5>データの取得中にエラーが発生しました</h5>
              <p>{fetchError}</p>
              <Button variant="outline-primary" size="sm" onClick={this.fetchSessions}>
                再読み込み
              </Button>
            </div>
          ) : currentSessions.length === 0 ? (
            <div className="sessions-empty">
              <div className="sessions-empty-icon">
                <FontAwesomeIcon icon="folder-open" />
              </div>
              <h5>該当するセッションデータが見つかりませんでした</h5>
              <p className="text-muted">
                {viewMode === 'month'
                  ? `${selectedMonth.format('YYYY年MM月')} にはデータが登録されていません。「全期間」ボタンを押すか、上部の「<」ボタンで過去の月へ移動してください。`
                  : '検索条件を変更するか、ユーザー・デバイスのフィルターを「すべて」にしてください。'}
              </p>
              {viewMode === 'month' && (
                <div className="mt-3">
                  <Button variant="primary" size="sm" onClick={() => this.handleViewModeChange('all')} style={{ marginRight: '8px' }}>
                    全期間のセッションを表示する
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => this.handleSetMonth(2026, 7)}>
                    2026年7月へ移動
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Table hover responsive className="sessions-table mb-0">
                <thead>
                  <tr>
                    <th>計測日時</th>
                    <th>デバイス</th>
                    <th>担当ユーザー</th>
                    <th>練習数</th>
                    <th>最高速度</th>
                    <th>合計距離</th>
                    <th style={{ textAlign: 'center', width: '240px' }}>ダウンロード</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSessions.map((session, index) => {
                    const startMoment = Moment(session.started_at);
                    const endMoment = session.ended_at ? Moment(session.ended_at) : null;
                    const userName = session.user
                      ? `${session.user.last_name || ''} ${session.user.first_name || ''}`.trim() || session.user.email
                      : '-';

                    return (
                      <tr key={index}>
                        <td>
                          <div className="session-date">{startMoment.format('YYYY/MM/DD (ddd)')}</div>
                          <div className="session-time">
                            {startMoment.format('HH:mm')}
                            {endMoment ? ` 〜 ${endMoment.format('HH:mm')}` : ''}
                          </div>
                        </td>
                        <td>
                          <span className="session-device-badge">
                            {session.device_id || 'IDなし'}
                          </span>
                          {session.device_type && (
                            <span className="text-muted ml-2" style={{ fontSize: '0.8rem' }}>
                              ({session.device_type})
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="session-user-badge">{userName}</span>
                        </td>
                        <td>
                          <Badge variant="info">{session.practices.length} 練習</Badge>
                        </td>
                        <td>
                          {session.max_speed ? `${Number(session.max_speed).toFixed(1)} km/h` : '-'}
                        </td>
                        <td>
                          {session.total_distance
                            ? `${(session.total_distance / 1000).toFixed(2)} km`
                            : '-'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div className="d-flex justify-content-center gap-2" style={{ gap: '6px' }}>
                            <Button
                              variant={session.session_url ? 'primary' : 'outline-secondary'}
                              size="sm"
                              disabled={!session.session_url}
                              onClick={() => this.handleDownloadRaw(session)}
                              title={session.session_url ? 'RAWデータ(CSV)をダウンロード' : 'RAWデータURLが登録されていません'}
                              style={{ fontWeight: '600' }}
                            >
                              <FontAwesomeIcon icon="file-csv" style={{ marginRight: '4px' }} />
                              CSV
                            </Button>
                            {session.excel_url && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => this.handleDownloadExcel(session)}
                                title="Excel集計データをダウンロード"
                                style={{ fontWeight: '600' }}
                              >
                                <FontAwesomeIcon icon="file-excel" style={{ marginRight: '4px' }} />
                                Excel
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* ページネーション & フッター情報 */}
              <div className="sessions-pagination">
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  全 <strong>{totalItems}</strong> 件中 {startIndex + 1} 〜 {Math.min(startIndex + itemsPerPage, totalItems)} 件を表示
                </div>
                <div>{this.renderPagination(totalPages)}</div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  authUser: state.auth.user,
  devices: (state.Device && state.Device.devices) || (state.device && state.device.devices) || [],
  players: (state.PlayerSelector && state.PlayerSelector.players) || [],
});

const mapDispatchToProps = (dispatch) => ({
  getDevices: () => dispatch(device.getDevices()),
  getPlayers: () => dispatch(playerSelector.getPlayers()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sessions);
